import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LightMotion } from "../components/LightMotion";
import { Icon, declaredIcon, type IconName } from "./NavigationIcon";
import type { HubAgent, PanelEntry, LocalClientInfo } from "../lib/api";
import type { SurfacePages } from "../lib/surfaceRequests";
import { agentHref, instanceStatus, readAgentView } from "../hub/api";
import { clientName, useLocalClients } from "../hub/LocalClients";
import { readLastWorkspace } from "../lib/lastWorkspace";
import { agentLanding, panelHref, sameWorkspace, workspaceEntries, workspaceHref, workspaceTitle } from "../lib/workspaces";
import { AccountMenu } from "./AccountMenu";
import { Avatar } from "./Avatar";
import ribbonUrl from "../assets/pa-ribbon.webp";
import { useOwnerShell, type OwnerPending } from "./ownerShell";
import "./OwnerNavigation.css";
import { connectionHref, localWorkHref } from "../connections/navigation";

export type OwnerDestination = "overview" | "agents" | "people" | "responses" | "settings" | "square" | "publish";
type Row = { key: OwnerDestination; label: string; icon: IconName };
/** Global destinations; requests remain beside the Agent that asked. */
const mainRows: Row[] = [
  { key: "overview", label: "首页", icon: "today" },
  { key: "publish", label: "公开发布", icon: "content" },
  { key: "square", label: "广场", icon: "wall" },
];
const footRows: Row[] = [
  { key: "settings", label: "设置", icon: "settings" },
];
export function OwnerNavigation({ agents, currentAgent = null, currentPanel, currentRoute, active, sessionId,
  activePanels, currentPending, version = 0, accountName = "你", onNavigate, connected = true, agentDestinations = {}, settingsHref, discovery = false, guest = false }: {
  agents: HubAgent[] | null; currentAgent?: string | null; currentPanel?: string; currentRoute?: string;
  activePanels?: PanelEntry[]; active?: OwnerDestination; sessionId?: string; version?: number | string;
  currentPending?: OwnerPending;
  accountName?: string; onNavigate?: (hash: string) => void; connected?: boolean;
  agentDestinations?: Record<string, string>;
  settingsHref?: string;
  discovery?: boolean;
  guest?: boolean;
}) {
  const [mobileOpen, showMobile] = useState(false);
  const navigation = useRef<HTMLElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const dismiss = useRef<HTMLButtonElement>(null);
  const owned = useOwnerShell();
  const directory = useLocalClients();
  const [closedClients, setClosedClients] = useState<string[]>([]);
  const currentRow = agents?.find(agent => agent.id === currentAgent);
  const currentGroup = currentRow?.location === "local" ? currentRow.client_id || "legacy-pending" : null;
  useEffect(() => {
    if (currentGroup) setClosedClients(old => old.includes(currentGroup) ? old.filter(id => id !== currentGroup) : old);
  }, [currentGroup, currentAgent]);
  const knownClients = directory.clients ?? [...new Map((agents || []).flatMap(agent => agent.client ? [agent.client] : [])
    .map(client => [client.id, client] as const)).values()];
  const clientGroups = new Map<string, { id: string; client: LocalClientInfo | null; agents: HubAgent[] }>();
  for (const agent of agents || []) {
    if (agent.location !== "local") continue;
    const id = agent.client_id || "legacy-pending";
    const client = agent.client_id ? knownClients.find(item => item.id === agent.client_id)
      || (agent.client?.id === agent.client_id ? agent.client : null) : null;
    let group = clientGroups.get(id);
    if (!group) { group = { id, client: client || null, agents: [] }; clientGroups.set(id, group); }
    group.agents.push(agent);
  }
  const toggleClient = (id: string, open: boolean) => setClosedClients(old => open
    ? old.includes(id) ? old.filter(item => item !== id) : old
    : old.includes(id) ? old : [...old, id]);

  const local = Boolean(onNavigate) && !currentAgent && agents === null;
  const globalHref = (key: OwnerDestination) => key === "settings" && settingsHref ? settingsHref : key === "square" || key === "publish" ? `/#/${key}` : local
    ? ({ overview: "#/today", agents: "#/today", people: "#/ip", responses: "#/approvals", settings: "#/connections" })[key]
    : `/#/${key}${key === "settings" && currentAgent ? `?${new URLSearchParams({ agent: currentAgent })}` : ""}`;
  const click = (event: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    if (!onNavigate || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault(); showMobile(false); onNavigate(hash);
  };
  // The settings page you are on is not a place to go to: a plain press on 设置
  // does nothing, where it used to drop the Agent it was set to (a press with a
  // modifier still opens it where the reader asked). The other destinations are
  // one address each, and pressing the address you are on already does nothing.
  const stay = (event: React.MouseEvent<HTMLAnchorElement>, here: boolean) => {
    if (!here || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return false;
    event.preventDefault();
    return true;
  };
  const localNavigate = onNavigate ? (hash: string) => { showMobile(false); onNavigate(hash); } : undefined;
  useEffect(() => {
    const close = () => showMobile(false);
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape" && !event.defaultPrevented) close(); };
    window.addEventListener("hashchange", close);
    window.addEventListener("keydown", escape);
    return () => { window.removeEventListener("hashchange", close); window.removeEventListener("keydown", escape); };
  }, []);
  useLayoutEffect(() => {
    if (!mobileOpen) return;
    const compact = window.matchMedia?.("(max-width: 1024px)");
    if (!compact?.matches) { showMobile(false); return; }
    const nav = navigation.current;
    const opener = toggle.current;
    if (!nav || !opener) return;
    const allowed = [nav, opener, dismiss.current].filter((item): item is HTMLElement => item !== null);
    const isolated = new Map<HTMLElement, string | null>();
    // The nav and workspace may share several ancestors. Isolate each outside branch,
    // including the frame itself, rather than only trapping keys in the outer document.
    const isolate = (parent: HTMLElement) => {
      for (const child of parent.children) {
        if (!(child instanceof HTMLElement) || allowed.includes(child)) continue;
        if (allowed.some(item => child.contains(item))) isolate(child);
        else if (!isolated.has(child)) {
          isolated.set(child, child.getAttribute("inert"));
          child.setAttribute("inert", "");
        }
      }
    };
    isolate(document.body);
    const observer = new MutationObserver(() => isolate(document.body));
    observer.observe(document.body, { childList: true, subtree: true });
    const within = (target: EventTarget | null) => target instanceof Node && (target === opener || nav.contains(target));
    const items = () => [opener, ...nav.querySelectorAll<HTMLElement>(
      "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]",
    )].filter(item => item.tabIndex >= 0 && !item.closest("[hidden], [inert]") && getComputedStyle(item).display !== "none");
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const stops = items();
      const first = stops[0];
      const last = stops.at(-1);
      if (!within(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      }
    };
    const focus = (event: FocusEvent) => { if (!within(event.target)) opener.focus(); };
    const resized = () => { if (!compact.matches) showMobile(false); };
    document.addEventListener("keydown", trap, true);
    document.addEventListener("focusin", focus);
    compact.addEventListener?.("change", resized);
    opener.focus();
    return () => {
      observer.disconnect();
      document.removeEventListener("keydown", trap, true);
      document.removeEventListener("focusin", focus);
      compact.removeEventListener?.("change", resized);
      for (const [element, before] of isolated) {
        if (before === null) element.removeAttribute("inert");
        else element.setAttribute("inert", before);
      }
      if (compact.matches && opener.isConnected) opener.focus();
    };
  }, [mobileOpen]);
  const agentNode = (agent: HubAgent) => <AgentNode key={agent.id} agent={agent} current={currentAgent === agent.id}
              pending={currentAgent === agent.id && currentPending ? currentPending : owned.pendingByAgent?.[agent.id]}
              resumeHref={currentAgent !== agent.id ? agentDestinations[agent.id] : undefined}
              online={currentAgent === agent.id ? connected : undefined}
              currentPanel={currentAgent === agent.id ? currentPanel : undefined} version={version}
              currentRoute={currentAgent === agent.id ? currentRoute : undefined}
              activePanels={currentAgent === agent.id ? activePanels : undefined}
              sessionId={currentAgent === agent.id ? sessionId : undefined} onNavigate={currentAgent === agent.id ? localNavigate : undefined} />;
  const row = (item: Row) => {
    const here = active === item.key;
    const inner = <><Icon name={item.icon} size={18} /><span>{item.label}</span></>;
    return <a key={item.key} className={`owner-nav-row${here ? " is-active" : ""}`} href={globalHref(item.key)}
      aria-current={here ? "page" : undefined} data-testid={`nav-owner-${item.key}`}
      onClick={event => {
        if (item.key === "settings") { if (!stay(event, here) && local) click(event, globalHref(item.key)); }
        else if (local) click(event, globalHref(item.key));
      }}>{inner}</a>;
  };
  const shown = (item: Row) => guest ? item.key === "square" : discovery || !["square", "publish"].includes(item.key);
  return <>
    <button ref={toggle} type="button" className="owner-nav-toggle" aria-label={mobileOpen ? "收起导航" : "打开导航"}
      aria-expanded={mobileOpen} aria-controls="owner-navigation" onClick={() => showMobile(value => !value)}><Icon name={mobileOpen ? "no" : "studio"} size={22} /></button>
    {mobileOpen ? <button ref={dismiss} className="owner-nav-dismiss" type="button" tabIndex={-1} aria-label="关闭导航" onClick={() => showMobile(false)} /> : null}
    <nav ref={navigation} id="owner-navigation" className="owner-navigation" data-open={mobileOpen || undefined} aria-label="涟漪导航">
    <div className="owner-nav-top">
      <a className="owner-brand" href={globalHref(guest ? "square" : "overview")}>
        <img className="brand-mark" src={ribbonUrl} alt="" /><span>涟漪</span>
      </a>
      <div className="owner-nav-main">
        <div className="owner-nav-list">
          {local ? <AgentNode current agent={null} online={connected} currentPanel={currentPanel} currentRoute={currentRoute}
            version={version} sessionId={sessionId} onNavigate={localNavigate} activePanels={activePanels}
            localWorkspace active={active === "overview"} pending={currentPending} /> : null}
          {mainRows.filter(item => shown(item) && !(local && item.key === "overview")).map(item => row(item))}
        </div>
        {guest || local ? null : <div className="owner-nav-group">
          <div className="owner-nav-label">
            <a href={globalHref("agents")} data-testid="nav-owner-agents" aria-current={active === "agents" ? "page" : undefined}>我的 IP</a>
            <a className="tree-new" href="/#/create" aria-label="新建 IP" title="新建 IP"><Icon name="plus" size={16} /></a>
          </div>
          <div className="owner-agent-tree">
            {(agents || []).filter(agent => agent.location !== "local").map(agentNode)}
            {[...clientGroups.values()].map(group => <details className="owner-client-group" key={group.id}
              open={!closedClients.includes(group.id)} onToggle={event => toggleClient(group.id, event.currentTarget.open)}>
              <summary className="owner-client-heading">
                <Icon name="down" size={12} className="owner-client-caret" />
                <span className="owner-client-name" data-quoted="true">{group.id === "legacy-pending" ? "待连接"
                  : group.client ? clientName(group.client, knownClients) : "电脑 · " + group.id.slice(-6)}</span>
                {group.client ? <small>{directory.error ? "状态待更新" : group.client.online ? "在线" : "离线"}</small> : null}
              </summary>
              <div className="owner-client-agents">{group.agents.map(agentNode)}</div>
            </details>)}
            {currentAgent && !agents?.length ? <AgentNode current agent={null} idOverride={currentAgent} online={connected} currentPanel={currentPanel}
              pending={currentPending}
              currentRoute={currentRoute}
              version={version} sessionId={sessionId} onNavigate={localNavigate} activePanels={activePanels} /> : null}
            {agents === null ? <p className="tree-note"><LightMotion state="working" compact />正在读取…</p> : null}
          </div>
        </div>}
      </div>
    </div>
    <div className="owner-nav-bottom">
      {guest ? <a className="owner-nav-row" href="/#/overview"><Icon name="person" size={18} /><span>登录</span></a> : <>
        {footRows.map(item => row(item))}
        <AccountMenu name={accountName} credit={owned.credit} onLogout={owned.onLogout} />
      </>}
    </div>
  </nav></>;
}

function AgentNode({ agent, current, currentPanel, currentRoute, version, sessionId, onNavigate, online, idOverride, activePanels, localWorkspace = false, active, resumeHref, pending }: {
  activePanels?: PanelEntry[]; localWorkspace?: boolean; active?: boolean;
  agent: HubAgent | null; idOverride?: string | null; current: boolean; currentPanel?: string; currentRoute?: string; version: number | string;
  sessionId?: string; onNavigate?: (hash: string) => void; online?: boolean;
  resumeHref?: string;
  pending?: OwnerPending;
}) {
  const [expanded, expand] = useState(current);
  useEffect(() => { if (current) expand(true); }, [current]);
  const [pages, setPages] = useState<SurfacePages["pages"] | null>(null);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const id = agent?.id ?? idOverride ?? null;
  const listed = agent?.online ?? true;
  const available = online ?? listed;
  // This page's own link to the Agent dropped while the directory still has
  // it: the page is reconnecting, and what the Agent offers stays listed.
  const connecting = online === false && listed && !(agent?.client_id && agent.desired_running === false);
  const shown = available || connecting;
  const wasOnline = useRef(false);
  useEffect(() => { if (online) wasOnline.current = true; }, [online]);
  const name = agent?.name || (localWorkspace ? "IP 工作区" : "当前 IP");
  const loadedFor = useRef(id);
  useEffect(() => {
    if (!expanded || !available || activePanels !== undefined) return;
    const controller = new AbortController();
    if (loadedFor.current !== id) { setPages(null); loadedFor.current = id; }
    setError(false);
    readAgentView<SurfacePages>(id, "/api/surface-pages", controller.signal, true).then(result => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(result.pages)) throw new Error("工作界面列表不完整");
      setPages(result.pages); setError(false);
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [id, expanded, available, version, refresh, activePanels, current]);
  const visiblePages = activePanels ?? pages;
  const target = (hash: string) => id ? agentHref(id, hash) : hash;
  const workspaces = workspaceEntries(visiblePages ?? []);
  const route = current ? localWorkHref(currentRoute) : undefined;
  const currentWork = currentPanel && route?.split("?")[0] === panelHref(currentPanel) ? route : undefined;
  const pageHash = (page: PanelEntry) => {
    return page.key === currentPanel && currentWork ? currentWork
      : workspaceHref(page, current ? sessionId : undefined, id ? `/agents/${encodeURIComponent(id)}` : "/");
  };
  // Where the Agent's name leads: straight into its one workspace when that is
  // all it offers (what the Agent home does on arrival), else its home page.
  // The open Agent's own panels are what its home decides by; the page list
  // stands in for an Agent that is not open.
  const landing = shown
    ? agentLanding(activePanels ?? visiblePages ?? [], readLastWorkspace(id ? `/agents/${encodeURIComponent(id)}` : undefined))
    : null;
  const homeHash = landing ? pageHash(landing) : "#/today";
  // The open Agent's name, pressed while its home is what is showing, leads
  // nowhere new: it does nothing and fetches nothing.
  const atHome = current && (landing
    ? currentPanel === landing.key
    : !currentPanel);
  const managementHash = connectionHref(undefined, sessionId,
    currentWork ?? (currentPanel ? panelHref(currentPanel, sessionId ? { session: sessionId } : {}) : homeHash));
  const click = (event: React.MouseEvent<HTMLAnchorElement>, hash: string, stay = false) => {
    if (!onNavigate || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (!stay) onNavigate(hash);
  };
  // Connected is the ordinary state and needs no word; the other two are what a
  // person has to be told, and a word beside the name says it (D437).
  const state = connecting ? wasOnline.current ? "正在重新连接" : "正在连接"
    : available ? error ? "状态未读到" : "" : agent ? instanceStatus(agent).label : "未连接";
  const here = localWorkspace ? Boolean(active) : current && !currentPanel;
  return <div className={`owner-agent-node${current ? " is-current" : ""}`}
    data-state={connecting ? "connecting" : !available ? agent?.runtime?.state === "sleeping" ? "sleeping" : "offline" : error ? "unread" : "online"}>
    <div className={`owner-agent-heading${here ? " is-active" : ""}`} data-state={here ? "active" : undefined}>
      {/* One per agent in the tree; a single-agent Studio has exactly one. */}
      <a href={resumeHref || target(homeHash)} data-testid={localWorkspace ? "nav-owner-overview" : "nav-agent-node"}
        onClick={event => click(event, homeHash, atHome)} aria-current={here ? "page" : undefined}>
        <Avatar name={name} identity={id} />
        <span className="owner-agent-name" data-quoted="true">{name}</span>
      </a>
      {state ? <span className="agent-state">{state}</span> : null}
      {pending?.state === "ready" ? pending.count && pending.href ? <a className="owner-agent-pending num"
        href={target(pending.href)} aria-label={`${name}，${pending.count} 项待回应`} title={`${pending.count} 项待回应，打开对话`}
        onClick={event => click(event, pending.href!)}>{pending.count}</a> : null
        : pending ? <span className="owner-agent-pending is-unknown" aria-label={`${name}，${pending.state === "reading" ? "正在检查待回应事项" : "待回应事项尚未检查"}`}
          title={pending.state === "reading" ? "正在检查待回应事项" : "待回应事项尚未检查"}>?</span> : null}
      {shown ? <button className="tree-toggle" type="button" aria-label={`${expanded ? "收起" : "展开"} ${name}`}
        aria-expanded={expanded} onClick={() => expand(value => !value)}><Icon name="down" size={14} /></button> : null}
    </div>
    {expanded && (shown || Boolean(agent?.client_id)) ? <div className="owner-agent-pages">
      {error ? <button className="btn btn-quiet tree-retry" type="button" onClick={() => setRefresh(value => value + 1)}>界面没有读到，重试</button>
        : visiblePages == null ? <p className="tree-note">{available ? <><LightMotion state="working" compact />正在读取界面…</> : "启动后可查看工作界面"}</p>
        : !workspaces.length ? <p className="tree-note">还没有工作界面</p>
        : workspaces.map(page => {
          const hash = pageHash(page);
          const selected = sameWorkspace(visiblePages.find(panel => panel.key === currentPanel), page);
          return <a key={page.key} className={selected ? "is-selected" : ""}
            href={target(hash)} onClick={event => click(event, hash, selected && currentPanel === page.key)} aria-current={selected ? "page" : undefined}>
            <Icon name={declaredIcon(page.icon)} size={16} /><span>{workspaceTitle(page)}</span>
          </a>;
        })}
      <a href={target(managementHash)} aria-label={`${name} 设置`} onClick={event => click(event, managementHash)}>
        <Icon name="settings" size={16} /><span>设置</span>
      </a>
    </div> : null}
  </div>;
}
